import apiClient from '../../services/api';

/**
 * Fetch fundamentals + financials + EPS trends for a list of tickers and
 * reshape the responses into the `tickerData` object scoring.js expects.
 *
 * Fundamentals is required — if that endpoint fails the whole bundle fails.
 * Financials and EPS trends are best-effort: if either errors we keep going
 * with empty maps so a preset that only uses fundamentals still scores.
 *
 *   {
 *     fundamentalsByTicker: { AAPL: {<row>}, ... },
 *     financialsByTicker:   { AAPL: { revenue: {2020:..., TTM:...}, ... }, ... },
 *     epsTrendsByTicker:    { AAPL: {<row>}, ... },
 *     years:                [2020, 2021, ..., 'TTM'],
 *   }
 *
 * The financials response is keyed `ratios[metric][ticker][year]`; this
 * helper transposes it to `byTicker[ticker][metric][year]` because that's
 * how scoring.js (getFieldValue / yoy()) reads it.
 */
export async function fetchTickerBundle(tickers, { financialsYears = 5 } = {}) {
  const [fundResp, finResp, epsResp] = await Promise.all([
    apiClient.post('/research/fundamentals/', { tickers }),
    apiClient
      .post('/research/financials/ratios/', { tickers, years: financialsYears })
      .catch(() => ({ data: null })),
    apiClient.post('/research/eps-trends/', { tickers }).catch(() => ({ data: null })),
  ]);

  const fundamentalsByTicker = {};
  for (const row of fundResp.data?.research_fundamentals || []) {
    if (row?.Ticker) fundamentalsByTicker[row.Ticker] = row;
  }

  const financialsByTicker = {};
  let years = [];
  if (finResp.data?.ratios) {
    years = finResp.data.years || [];
    for (const [metric, byTicker] of Object.entries(finResp.data.ratios)) {
      for (const [ticker, byYear] of Object.entries(byTicker || {})) {
        if (!financialsByTicker[ticker]) financialsByTicker[ticker] = {};
        financialsByTicker[ticker][metric] = byYear;
      }
    }
  }

  const epsTrendsByTicker = {};
  for (const row of epsResp.data?.eps_trends || []) {
    if (row?.Ticker) epsTrendsByTicker[row.Ticker] = row;
  }

  return { fundamentalsByTicker, financialsByTicker, epsTrendsByTicker, years };
}

/**
 * Collect every ticker referenced by a preset for an Apply run:
 * the row tickers themselves, plus any benchmarkTicker on any column.
 * Returned in upper-case, deduped.
 */
export function collectAllTickers(rowTickers, columns) {
  const out = new Set();
  for (const t of rowTickers || []) {
    if (typeof t === 'string' && t.trim()) out.add(t.trim().toUpperCase());
  }
  for (const col of columns || []) {
    if (col?.benchmarkTicker) out.add(String(col.benchmarkTicker).toUpperCase());
  }
  return Array.from(out);
}
